<template>
  <v-dialog
    :value="value"
    width="900px"
    @click:outside="$emit('input', false)"
  >
    <v-card>
      <v-card-title class="pa-0 sticky-header">
        <v-toolbar 
          color="primary"
          dark
        >
          <v-btn
            class="mr-2"
            icon
            @click="$emit('input', false)"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
          Select Servers
          <v-spacer />
          <lock-timer
            v-bind="getLockTimerSettings(
              [itemData],
              {
                recordType: apiRecordCommand,
                include_fields: ['seatings', 'servers'],
                children: true
              },
              () => $emit('input', false)
            )"
          />
          <v-btn
            :color="isDirty ? 'green' : 'primary darken-1'"
            :light="isDirty"
            @click="savePageData"
          >
            Save
          </v-btn>
        </v-toolbar>
      </v-card-title>
      <api-errors :errors="errors" />
      <v-row 
        no-gutters
        class="pt-2"
      >
        <v-col
          cols="12"
          sm="12"
          md="7"
          class="px-3 d-flex justify-center"
        >
          <v-switch 
            v-model="showSelected"
            label="Show Selected Servers"
          />
        </v-col>
        <v-col
          cols="12"
          sm="5"
          md="5"
          class="px-3"
        >
          <v-text-field
            v-model="search"
            label="Search"
            clearable
            append-icon="mdi-magnify"
          />
        </v-col>
      </v-row>
      <v-row no-gutters>
        <div 
          class="d-flex hover-cursor"
          @click="clickAll(showSelected ? false : !allSelected)"
        >
          <v-simple-checkbox
            :value="allSelected || showSelected"
            :ripple="false"
            label="Select All"
            class="px-3"
            color="primary"
            @input="clickAll(showSelected ? false : !allSelected)"
          />
          <div class="d-flex align-self-center">
            <div class="v-label theme--light hover-cursor">
              Select All
            </div>
          </div>
        </div>
      </v-row>
      <v-fade-transition
        group
        hide-on-leave
      >
        <v-row 
          v-if="awaitingApiResponse"
          key="servers-transition-1"
          no-gutters
          class="d-flex justify-center"
        >
          <v-progress-linear indeterminate />
          <div class="d-flex justify-center">
            Loading...
          </div>
        </v-row>
        <v-row
          v-else-if="showSelected"
          key="servers-transition-2"
          no-gutters
        >
          <v-col
            v-for="(server, index) of selectedServersDisplay"
            :key="`selected-server-${index}`"
            cols="12"
            sm="6"
            md="3"
            class="px-3 pt-3"
          >
            <v-fade-transition leave-absolute>
              <div
                class="d-flex hover-cursor"
                @click="selectServer(server.id)"
              >
                <v-simple-checkbox
                  class="px-3"
                  value
                  :ripple="false"
                  color="primary"
                  @input="selectServer(server.id)"
                />
                <div class="v-label theme--light text-no-wrap align-self-center hover-cursor">
                  {{ server.name }}
                </div>
              </div>
            </v-fade-transition>
          </v-col>
        </v-row>
        <v-row 
          v-else-if="!showSelected"
          key="servers-transition-3"
          no-gutters
        >
          <v-col
            v-for="(server, index) of allServers"
            :key="`all-servers-${index}`"
            cols="12"
            sm="6"
            md="3"
            class="px-3 pt-3"
          >
            <v-fade-transition leave-absolute>
              <div
                class="d-flex hover-cursor"
                @click="selectServer(server.id)"
              >
                <v-simple-checkbox
                  class="px-3"
                  :value="showSelected || !!selectedServersMap[server.id]"
                  :ripple="false"
                  color="primary"
                  @input="selectServer(server.id)"
                />
                <div class="v-label theme--light text-no-wrap align-self-center hover-cursor">
                  {{ server.name }}
                </div>
              </div>
            </v-fade-transition>
          </v-col>
        </v-row>
        <v-card 
          v-else
          key="servers-transition-3"
          class="elevation-0 text-center"
        >
          <div class="font-weight-bold">
            No Servers to Show
          </div>
        </v-card>
      </v-fade-transition>
      <div class="py-3" />
    </v-card>
  </v-dialog>
</template>

<script>
import recordLockHelper from '@/ft-vue-core/mixins/recordLockHelper'
export default {
  name: 'SelectServersDialog',
  mixins: [recordLockHelper],
  props: {
    value: Boolean,
    mealPeriodId: Number,
    locationId: Number,
    servers: {
      type: Array,
      default () {return []}
    },
    forEvent: Boolean,
    eventId: Number,
    date: [String, Number],
  },
  data () {
    return {
      awaitingApiResponse: 0,
      itemData: {},
      isDirty: false,
      showSelected: false,
      search: undefined,
      allServers: [],
      selectedServersMap: {},
      recordLockConfiguration: {},
      apiRecordCommand: 'diningMealPeriod',
    }
  },
  computed: {
    selectedServersDisplay () {
      let result = new Array()
      for (let server of this.allServers) {
        if (this.selectedServersMap[server.id]) result.push(server)
      }
      return result
    },
    unselectedServers () {
      let result = new Array()
      for (let server of this.allServers) {
        if (!this.selectedServersMap[server.id]) result.push(server)
      }
      return result
    },
    allSelected () {
      return Object.keys(this.selectedServersMap)?.reduce((acc, r) => {
        if (this.selectedServersMap[r]) acc++
        return acc
      }, 0) === this.allServers.length
    },
    allServersMap () {
      return this.allServers?.reduce((acc, r) => {
        acc[r.id] = r
        return acc
      }, {}) ?? {}
    }
  },
  watch: {
    value (newVal) {
      if (newVal) {
        this.isDirty = false
        this.selectedServersMap = this.servers.reduce((acc, r) => {
          acc[r.serverId] = true
          return acc
        }, {}) ?? {}
        this.loadPageData()
      } else {
        this.showSelected = false
        this.search = undefined
        this.selectedServers = []
        this.selectedServersMap = {}
        this.awaitingApiResponse = 0
        // this.apiErrorMessage = undefined
        const {recordLockKey} = this.itemData
        this.$ftAPI().get(this.addResourceErrorHandler({
          command: 'releaseLock',
          params: {record_key: recordLockKey},
        }))
        this.itemData = {}
        this.errors = [],
        this.$set(this, 'recordLockConfiguration', {refreshKey: 'exit'})
      }
    },
    selectedServersMap: {
      handler (newVal, oldVal) {
        if (JSON.stringify(oldVal) !== '{}') this.isDirty = true
      },
      immediate: true
    },
  },
  methods: {
    selectServer (serverId) {
      this.$set(this.selectedServersMap, serverId, !this.selectedServersMap[serverId])
    },
    clickAll (value) {
      this.showSelected = value
      if (!value) this.selectedServersMap = {}
      else this.selectedServersMap = this.allServers.reduce((acc, r) => {
        acc[r.id] = true
        return acc
      }, []) ?? []
    },
    loadPageData () {
      
      // Build base command object for use with either `diningEvent` or `diningMealPeriod`
      const baseCommandObject = this.withResourceLockHandler({
        load: r => this.itemData = r?.[0] ?? {},
        afterLoad: () => {
          ['endTime', 'refreshKey'].forEach(f => this.$set(this.recordLockConfiguration, f, this.itemData.recordLockExpiration))
          this.awaitingApiResponse -= 1
          this.selectedServersMap = this.itemData.servers?.reduce((acc, s) => {
            acc[s.serverId] = true
            return acc
          }, {}) ?? {}
        },
        error: err => {
          this.awaitingApiResponse -= 1
          this.errors.push(err.message)
        },
        params: {children: true, include_fields: ['seatings', 'servers']}
      })

      // Build out `commands` Array and issue GET request
      const commands = [
        {
          command: 'diningServer',
          load: this.allServers,
          loading: this.isLoading,
          afterLoad: () => this.awaitingApiResponse -= 1,
          params: {
            children: true,
            filter: [{field: 'active', operator: 'EQUAL', values: [true]}],
            filter_methods: this.forEvent ? {filterByInLocation: [this.locationId]} : {filterByInMealPeriod: [this.mealPeriodId]}
          }
        },
      ]
      if (this.forEvent) {
        commands.push({
          command: 'diningEvent',
          ...baseCommandObject,
          params: {
            ...baseCommandObject.params,
            filter: [{field: 'id', operator: 'EQUAL', values: [this.eventId]}],
          }
        })
      } else {
        commands.push({
          command: 'diningMealPeriod',
          ...baseCommandObject,
          params: {
            ...baseCommandObject.params,
            filter: [{field: 'id', operator: 'EQUAL', values: [this.mealPeriodId]}],
            filter_methods: {filterServersByDate: [this.date]},
          }
        })
      }
      this.awaitingApiResponse = commands.length
      this.$ftAPI().get(this.addResourceErrorHandler(commands))
    },
    savePageData () {
      const command = {
        command: this.forEvent ? 'diningEvent' : 'diningSaveMealPeriodServersByDate',
        afterLoad: () => {
          this.awaitingApiResponse -= 1
          this.$emit('refresh-meal-period')
          this.$emit('refresh')
          this.$emit('input', false)
        },
        params: {
          children: true,
          include_fields: ['seatings']
        }
      }
      this.awaitingApiResponse = 1
      const mapServers = () => {
        return Object.entries(this.selectedServersMap).reduce((acc, e) => {
          const [key, value] = e
          let server = {serverId: parseInt(key)}
          if (!this.forEvent) server.date = this.date
          if (value) acc.push(server)
          return acc
        }, [])
      }
      this.$ftAPI().put(this.addResourceErrorHandler(command), 
        this.forEvent 
          ? {...this.itemData, servers: mapServers()}
          : {mealPeriod: this.itemData, date: this.date, servers: mapServers()}
      )
    },
  }
}
</script>

<style scoped>
.hover-cursor:hover {
  cursor: pointer;
}
.sticky-header {
  position: sticky;
  top: 0px;
  z-index: 2;
}
</style>